//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { type DocumentQuery, type Ref, type Timestamp } from '@hcengineering/core'

import { type Document } from './types'

/**
 * How long a trashed document stays recoverable, expressed in calendar months.
 *
 * @public
 */
export const TRASH_RETENTION_MONTHS = 1

/** Days in the shortest calendar month — February outside a leap year. */
const MIN_DAYS_IN_CALENDAR_MONTH = 28

const MS_PER_DAY = 24 * 60 * 60 * 1000

/** Number of days in the UTC month `monthIndex` of `year`, overflow allowed. */
function daysInUtcMonth (year: number, monthIndex: number): number {
  // Day 0 of the next month is the last day of this one.
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate()
}

/**
 * Moment a document trashed at `trashedOn` stops being recoverable.
 *
 * Retention is one *calendar* month, so the expiry keeps the day of month where
 * that day exists and clamps to the last day of the target month where it does
 * not: trashing on January 31 expires on February 28 (or 29 in a leap year).
 *
 * The arithmetic is done in UTC on purpose. Local-time `Date` accessors would
 * make the answer depend on the host's timezone, so the same document would
 * expire at different instants on two servers, and a DST transition inside the
 * retention window would silently move the deadline by an hour.
 *
 * @public
 */
export function getTrashExpiry (trashedOn: Timestamp): Timestamp {
  const from = new Date(trashedOn)

  const year = from.getUTCFullYear()
  const targetMonth = from.getUTCMonth() + TRASH_RETENTION_MONTHS
  const dayOfMonth = Math.min(from.getUTCDate(), daysInUtcMonth(year, targetMonth))

  // Date.UTC normalises a month index past 11 into the following year.
  return Date.UTC(
    year,
    targetMonth,
    dayOfMonth,
    from.getUTCHours(),
    from.getUTCMinutes(),
    from.getUTCSeconds(),
    from.getUTCMilliseconds()
  )
}

/**
 * Whether a document trashed at `trashedOn` is past its retention window.
 *
 * @public
 */
export function isTrashExpired (trashedOn: Timestamp, now: Timestamp): boolean {
  return getTrashExpiry(trashedOn) <= now
}

/**
 * Lower bound on `trashedOn` for documents that may have expired by `now`.
 *
 * Clamped month addition has no exact inverse: on February 28 every document
 * trashed on January 29, 30 and 31 expires, yet subtracting a calendar month
 * from February 28 yields only January 28. A threshold built that way
 * *under-selects* and would keep those documents forever.
 *
 * So this deliberately over-selects instead. A calendar month is never shorter
 * than 28 days, so anything that has expired was trashed at least 28 days ago,
 * and `trashedOn <= now - 28 days` is a superset of the expired set. Callers
 * must re-check each candidate with {@link isTrashExpired}; the query only
 * narrows how much of Trash has to be read.
 *
 * @public
 */
export function getTrashPurgeCandidateThreshold (now: Timestamp): Timestamp {
  return now - TRASH_RETENTION_MONTHS * MIN_DAYS_IN_CALENDAR_MONTH * MS_PER_DAY
}

/**
 * Query fragment selecting documents that are *not* in Trash.
 *
 * Every surface that lists live documents must spread this into its query.
 * Documents created before the feature have no `trashedOn` at all, which both
 * the in-memory matcher and the Postgres adapter treat as `null`.
 *
 * @public
 */
export const notTrashedQuery: DocumentQuery<Document> = { trashedOn: null }

/**
 * Query fragment selecting Trash entries: documents the user trashed directly.
 *
 * Descendants dragged into Trash alongside their ancestor carry a `trashedRoot`
 * and are deliberately excluded — Trash lists subtrees, not individual nodes.
 *
 * @public
 */
export const trashedQuery: DocumentQuery<Document> = { trashedOn: { $ne: null }, trashedRoot: null }

/**
 * Whether the document is currently in Trash, directly or through an ancestor.
 *
 * @public
 */
export function isTrashed (doc: Pick<Document, 'trashedOn'>): boolean {
  return doc.trashedOn != null
}

/**
 * The Trash entry `doc` belongs to — itself, or the ancestor it was dragged in
 * with.
 *
 * Only entries can be restored: a descendant captured alongside its ancestor
 * carries a `trashedRoot` and goes back as part of that subtree, never on its
 * own. Resolving through the marker lets a surface holding *any* trashed
 * document — a deep link into the middle of a trashed subtree, say — name the
 * restore that actually applies to it, and check permissions against the
 * document that restore would touch.
 *
 * The answer is meaningless for a live document, which is why callers must
 * establish {@link isTrashed} first: a document with no `trashedRoot` is its own
 * entry as far as this can tell, trashed or not.
 *
 * @public
 */
export function getTrashEntryRef (doc: Pick<Document, '_id' | 'trashedRoot'>): Ref<Document> {
  return doc.trashedRoot ?? doc._id
}
